import {IAdvancedFilterGroup, IFilter, IFilterGroup} from './advanced-filter-group.interface';
import {getStockValueType, StockItemValTypeStr} from '../../../../shared-utilities/models-old/stock-stuff/stock-item-value-type';
import {getDeepCopyOfObject} from '../../../../shared/shared-utils/object/object.utils';

/**
 * Maps a stock grid column to the advanced-filter column type.
 */
export function resolveAdvancedFilterColumnType(column: string | undefined, columnType?: string): string {
  if (!column) {
    return columnType ?? 'string';
  }

  const stockValueType: StockItemValTypeStr | null = getStockValueType(column as never);
  if (stockValueType === 'string[]') {
    return 'string[]';
  }
  if (stockValueType === 'string') {
    return 'string';
  }
  if (stockValueType === 'number' || stockValueType === 'date' || stockValueType === 'boolean') {
    return stockValueType;
  }
  return columnType ?? 'string';
}

/**
 * Normalizes filter metadata before applying to the grid (resolves column types, clears presence values).
 */
export function normalizeAdvancedFilterGroup(advancedFilterGroup: IAdvancedFilterGroup): IAdvancedFilterGroup {
  const normalized = migrateAdvancedFilterGroupStructure(advancedFilterGroup);
  normalizeAdvancedFilterGroupFilterGroup(normalized.filterGroup);
  return normalized;
}

/**
 * Returns the next available identifier within a filter group tree.
 */
function getNextFilterTreeId(filterGroup: IFilterGroup): number {
  let maxId = filterGroup.id;
  const walkGroup = (group: IFilterGroup): void => {
    maxId = Math.max(maxId, group.id);
    group.filtersAndGroups.forEach((item: IFilter | IFilterGroup): void => {
      if ('filtersAndGroups' in item) {
        walkGroup(item);
        return;
      }
      maxId = Math.max(maxId, item.id);
    });
  };
  walkGroup(filterGroup);
  return maxId + 1;
}

/**
 * Ensures saved filters use a root container with one or more top-level filter groups.
 */
export function migrateAdvancedFilterContainerStructure(filterGroup: IFilterGroup): IFilterGroup {
  const container = getDeepCopyOfObject(filterGroup);
  const hasDirectFilters = container.filtersAndGroups.some(
    (item: IFilter | IFilterGroup): boolean => !('filtersAndGroups' in item),
  );

  if (hasDirectFilters) {
    const wrappedGroupId = getNextFilterTreeId(container);
    const wrappedGroup: IFilterGroup = {
      id: wrappedGroupId,
      operator: 'AND',
      filtersAndGroups: container.filtersAndGroups,
    };
    container.filtersAndGroups = [wrappedGroup];
  }

  if (container.filtersAndGroups.length === 0) {
    const groupId = getNextFilterTreeId(container);
    const filterId = groupId + 1;
    container.filtersAndGroups = [{
      id: groupId,
      operator: 'AND',
      filtersAndGroups: [{id: filterId} as IFilter],
    }];
  }

  return container;
}

/**
 * Migrates an advanced filter group to the container + top-level groups structure.
 */
export function migrateAdvancedFilterGroupStructure(advancedFilterGroup: IAdvancedFilterGroup): IAdvancedFilterGroup {
  const migrated = getDeepCopyOfObject(advancedFilterGroup);
  migrated.filterGroup = migrateAdvancedFilterContainerStructure(migrated.filterGroup);
  return migrated;
}

function normalizeAdvancedFilterGroupFilterGroup(filterGroup: IFilterGroup): void {
  filterGroup.filtersAndGroups.forEach((item: IFilter | IFilterGroup): void => {
    if ('filtersAndGroups' in item) {
      normalizeAdvancedFilterGroupFilterGroup(item);
      return;
    }

    if (item.column) {
      item.columnType = resolveAdvancedFilterColumnType(item.column, item.columnType);
    }

    if (item.filterType === 'partially equal to') {
      item.filterType = 'contains';
    }

    if (item.filterType === 'is empty' || item.filterType === 'is not empty') {
      item.value = '';
    }
  });
}

/**
 * Returns a short display label for a resolved advanced-filter column type.
 */
export function getAdvancedFilterColumnTypeLabel(columnType: string | undefined): string {
  switch (columnType) {
    case 'string[]':
      return 'array';
    case 'boolean':
      return 'yes/no';
    default:
      return columnType ?? 'string';
  }
}

/**
 * True when a date value is missing or the Typesense never-counted placeholder (epoch 0 / 1970-01-01).
 */
function isAdvancedFilterNeverCountedDate(value: unknown): boolean {
  if (value == null || value === '') {
    return true;
  }
  const ms = new Date(value as string | number | Date).getTime();
  return Number.isNaN(ms) || ms <= 0;
}

/**
 * Date-day filters: olderThan true = last date is before (today - days).
 * Never-counted last stock take dates are included only for older-than filters.
 */
function matchesAdvancedFilterDateAge(
  value: unknown,
  days: number,
  olderThan: boolean,
  column: string,
): boolean {
  if (Number.isNaN(days)) {
    return false;
  }
  const includeNeverCounted = olderThan && column === 'lastStockTake';
  if (isAdvancedFilterNeverCountedDate(value)) {
    return includeNeverCounted;
  }
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  const itemMs = new Date(value as string | number | Date).getTime();
  if (olderThan) {
    return itemMs < cutoff.getTime();
  }
  return itemMs > cutoff.getTime();
}

/**
 * Returns whether a field value should be treated as empty for client-side advanced filtering.
 */
function isAdvancedFilterFieldValueEmpty(value: unknown, column: string | undefined, columnType?: string): boolean {
  if (!column) {
    return false;
  }

  const resolvedColumnType = resolveAdvancedFilterColumnType(column, columnType);
  if (value === null || value === undefined) {
    return true;
  }

  if (resolvedColumnType === 'string') {
    const normalized = String(value).trim();
    return normalized === '' || normalized === '~~';
  }

  if (resolvedColumnType === 'string[]') {
    return !Array.isArray(value) || value.length === 0;
  }

  return false;
}

//#region FUNCTION applyAdvancedFilterGroupFilterToData
/**
 * Applies an advanced filter to a dataset.
 *
 * @template DataType - The type of the data elements.
 * @param {DataType[]} data - The dataset to filter.
 * @param {IFilter} filter - The filter criteria to apply.
 * @returns {DataType[]} - The filtered dataset.
 */
export function applyAdvancedFilterGroupFilterToData<DataType>(data: DataType[], filter: IFilter): DataType[] {
  return data.filter((item: DataType): boolean => {
    switch (filter.filterType) {

      case 'equal to':
        switch (filter.columnType) {
          case 'number':
            return Number(item[filter.column]) === Number(filter.value);
          case 'string':
            return String(item[filter.column]).toUpperCase() === String(filter.value).toUpperCase();
          case 'date':
            const eqDateValue = new Date(String(filter.value)).getTime();
            const eqItemDate = new Date(item[filter.column]).getTime();
            return eqItemDate === eqDateValue;
          default:
            break;
        }
        break;

      case 'greater than':
        switch (filter.columnType) {
          case 'number':
            return Number(item[filter.column]) > Number(filter.value);
          case 'date':
            const gtDateValue = new Date(String(filter.value)).getTime();
            const gtItemDate = new Date(item[filter.column]).getTime();
            return gtItemDate > gtDateValue;
          case 'string':
          default:
            break;
        }
        break;

      case 'greater than or equal to':
        switch (filter.columnType) {
          case 'number':
            return Number(item[filter.column]) >= Number(filter.value);
          case 'date':
            const gteDateValue = new Date(String(filter.value)).getTime();
            const gteItemDate = new Date(item[filter.column]).getTime();
            return gteItemDate >= gteDateValue;
          case 'string':
          default:
            break;
        }
        break;

      case 'less than':
        switch (filter.columnType) {
          case 'number':
            return Number(item[filter.column]) < Number(filter.value);
          case 'date':
            const ltDateValue = new Date(String(filter.value)).getTime();
            const ltItemDate = new Date(item[filter.column]).getTime();
            return ltItemDate < ltDateValue;
          case 'string':
          default:
            break;
        }
        break;

      case 'less than or equal to':
        switch (filter.columnType) {
          case 'number':
            return Number(item[filter.column]) <= Number(filter.value);
          case 'date':
            const lteDateValue = new Date(String(filter.value)).getTime();
            const lteItemDate = new Date(item[filter.column]).getTime();
            return lteItemDate <= lteDateValue;
          case 'string':
          default:
            break;
        }
        break;

      case 'not equal to':
        switch (filter.columnType) {
          case 'number':
            return Number(item[filter.column]) !== Number(filter.value);
          case 'string':
            return String(item[filter.column]).toUpperCase() !== String(filter.value).toUpperCase();
          case 'date':
            const neDateValue = new Date(String(filter.value)).getTime();
            const neItemDate = new Date(item[filter.column]).getTime();
            return neItemDate !== neDateValue;
          default:
            break;
        }
        break;

      case 'contains': {
        const resolvedType = resolveAdvancedFilterColumnType(filter.column, filter.columnType);
        const searchValue = String(filter.value).toUpperCase();
        if (resolvedType === 'string') {
          return String(item[filter.column]).toUpperCase().includes(searchValue);
        }
        if (resolvedType === 'string[]') {
          const values = item[filter.column];
          if (!Array.isArray(values)) {
            return false;
          }
          return values.some((entry: unknown): boolean => String(entry).toUpperCase().includes(searchValue));
        }
        break;
      }

      case 'does not contain': {
        const resolvedType = resolveAdvancedFilterColumnType(filter.column, filter.columnType);
        const searchValue = String(filter.value).trim();
        if (!searchValue) {
          return true;
        }
        if (resolvedType === 'string') {
          const fieldValue = String(item[filter.column] ?? '');
          const wordPattern = new RegExp(`\\b${searchValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
          return !wordPattern.test(fieldValue);
        }
        if (resolvedType === 'string[]') {
          const values = item[filter.column];
          if (!Array.isArray(values)) {
            return true;
          }
          return !values.some((entry: unknown): boolean => String(entry).toUpperCase() === searchValue.toUpperCase());
        }
        break;
      }

      case 'starts with': {
        const resolvedType = resolveAdvancedFilterColumnType(filter.column, filter.columnType);
        if (resolvedType === 'string') {
          return String(item[filter.column] ?? '').toUpperCase().startsWith(String(filter.value).toUpperCase());
        }
        break;
      }

      case 'ends with': {
        const resolvedType = resolveAdvancedFilterColumnType(filter.column, filter.columnType);
        if (resolvedType === 'string') {
          return String(item[filter.column] ?? '').toUpperCase().endsWith(String(filter.value).toUpperCase());
        }
        break;
      }

      case 'between': {
        const parts = String(filter.value ?? '').split(',');
        if (parts.length !== 2) {
          return false;
        }
        const minRaw = parts[0].trim();
        const maxRaw = parts[1].trim();
        if (!minRaw || !maxRaw) {
          return false;
        }
        const resolvedType = resolveAdvancedFilterColumnType(filter.column, filter.columnType);
        if (resolvedType === 'number') {
          const itemValue = Number(item[filter.column]);
          const min = Number(minRaw);
          const max = Number(maxRaw);
          if (Number.isNaN(itemValue) || Number.isNaN(min) || Number.isNaN(max)) {
            return false;
          }
          return itemValue >= min && itemValue <= max;
        }
        if (resolvedType === 'date') {
          const itemDate = new Date(item[filter.column]).getTime();
          const minDate = new Date(minRaw).getTime();
          const maxDate = new Date(maxRaw).getTime();
          if (Number.isNaN(itemDate) || Number.isNaN(minDate) || Number.isNaN(maxDate)) {
            return false;
          }
          return itemDate >= minDate && itemDate <= maxDate;
        }
        break;
      }

      case 'is empty':
        if (!filter.column) {
          return false;
        }
        return isAdvancedFilterFieldValueEmpty(item[filter.column], filter.column, filter.columnType);

      case 'is not empty':
        if (!filter.column) {
          return false;
        }
        return !isAdvancedFilterFieldValueEmpty(item[filter.column], filter.column, filter.columnType);

      case 'greater than (x) days':
      case 'less than (x) days': {
        const resolvedType = resolveAdvancedFilterColumnType(filter.column, filter.columnType);
        if (resolvedType !== 'date' || !filter.column) {
          break;
        }
        return matchesAdvancedFilterDateAge(
          item[filter.column],
          Number(filter.value),
          filter.filterType === 'less than (x) days',
          filter.column,
        );
      }

      default:
        break;


    }
  });
}

//#endregion

//#region FUNCTION applyAdvancedFilterGroupFilterGroupToData
/**
 * Applies an advanced filter group to a dataset.
 *
 * @template DataType - The type of the data elements.
 * @param {DataType[]} data - The dataset to filter.
 * @param {IFilterGroup} filterGroup - The group of filters to apply.
 * @returns {DataType[]} - The filtered dataset.
 */
export function applyAdvancedFilterGroupFilterGroupToData<DataType>(data: DataType[], filterGroup: IFilterGroup): DataType[] {
  const {filtersAndGroups, operator} = filterGroup;
  return filtersAndGroups.reduce((filteredData: DataType[], filterOrGroup: IFilterGroup | IFilter): (DataType)[] => {
    if ('filtersAndGroups' in filterOrGroup) {
      const groupResult = applyAdvancedFilterGroupFilterGroupToData(data, filterOrGroup);
      if (operator === 'AND') {
        return filteredData.filter((item: DataType) => groupResult.includes(item));
      } else {
        return [...filteredData, ...groupResult];
      }
    } else {
      const filterResult = applyAdvancedFilterGroupFilterToData(data, filterOrGroup);
      if (operator === 'AND') {
        return filteredData.filter((item: DataType) => filterResult.includes(item));
      } else {
        return [...filteredData, ...filterResult];
      }
    }
  }, operator === 'AND' ? data : []);
}

//#endregion

//#region FUNCTION applyAdvancedFilterGroupToData
/**
 * Applies an advanced filter group to a dataset.
 *
 * @template DataType - The type of the data elements.
 * @param {DataType[]} data - The dataset to filter.
 * @param {IAdvancedFilterGroup} advancedFilterGroup - The advanced filter group to apply.
 * @returns {DataType[]} - The filtered dataset.
 */
export function applyAdvancedFilterGroupToData<DataType>(data: DataType[], advancedFilterGroup: IAdvancedFilterGroup): DataType[] {
  return applyAdvancedFilterGroupFilterGroupToData<DataType>(data, advancedFilterGroup?.filterGroup);
}

//#endregion

/**
 * Counts individual filter conditions within a filter group tree.
 */
export function countAdvancedFilterConditions(filterGroup: IFilterGroup | undefined): number {
  if (!filterGroup?.filtersAndGroups?.length) {
    return 0;
  }

  return filterGroup.filtersAndGroups.reduce((count: number, item: IFilter | IFilterGroup): number => {
    if ('filtersAndGroups' in item) {
      return count + countAdvancedFilterConditions(item);
    }
    return count + 1;
  }, 0);
}

/**
 * Counts nested filter groups (excluding the root group wrapper).
 */
/**
 * Counts direct condition rows in a single filter group (not nested sub-groups).
 */
export function countDirectFilterConditions(filterGroup: IFilterGroup | undefined): number {
  if (!filterGroup?.filtersAndGroups?.length) {
    return 0;
  }

  return filterGroup.filtersAndGroups.filter((item: IFilter | IFilterGroup): boolean => !('filtersAndGroups' in item)).length;
}

export function countAdvancedFilterNestedGroups(filterGroup: IFilterGroup | undefined): number {
  if (!filterGroup?.filtersAndGroups?.length) {
    return 0;
  }

  return filterGroup.filtersAndGroups.reduce((count: number, item: IFilter | IFilterGroup): number => {
    if ('filtersAndGroups' in item) {
      return count + 1 + countAdvancedFilterNestedGroups(item);
    }
    return count;
  }, 0);
}

/**
 * Counts top-level filter groups within the root container.
 */
export function countTopLevelFilterGroups(filterGroup: IFilterGroup | undefined): number {
  if (!filterGroup?.filtersAndGroups?.length) {
    return 0;
  }

  const topLevelGroups = filterGroup.filtersAndGroups.filter(
    (item: IFilter | IFilterGroup): boolean => 'filtersAndGroups' in item,
  ).length;

  if (topLevelGroups > 0) {
    return topLevelGroups;
  }

  return filterGroup.filtersAndGroups.some((item: IFilter | IFilterGroup): boolean => !('filtersAndGroups' in item)) ? 1 : 0;
}
